import { useCallback, useRef, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  Select,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { VisibilityOffRounded as VisibilityOffIcon, VisibilityRounded as VisibilityIcon } from '@material-ui/icons';

import { User } from '../../types/user.type';
import { UserRoles } from '../../types/user-role.type';
import { useAdminCreateUser } from '../../hooks/admin-create-user.hook';
import {
  AdminCreateUserAddressDTO,
  AdminCreateUserRequestDTO,
  CREATABLE_USER_ROLES,
  CREATABLE_USER_ROLE_LABELS,
} from '../../types/dtos/admin-create-user.dto';
import AddressAutocomplete from '../../../shared/components/Forms/AddressAutocomplete.component';
import { ParsedAddress, buildAddressLabel } from '../../../shared/components/Forms/parse-google-address.util';
import PhoneNumberField, { isPhoneNumberIncomplete } from '../../../shared/components/Forms/PhoneNumberField.component';
import {
  ADDRESS_COMMENT_LABEL,
  ADDRESS_TITLE_LABEL,
  ZIP_CODE_FORMAT_ERROR,
  ZIP_CODE_PATTERN,
} from '../../../shared/components/Forms/address-form.constants';
import { isValidEmail } from '../../../shared/utils/is-valid-email.util';
import {
  EMAIL_INVALID_ERROR,
  FIRSTNAME_REQUIRED_ERROR,
  LASTNAME_REQUIRED_ERROR,
  PHONE_NUMBER_LENGTH_ERROR,
  PHONE_NUMBER_REQUIRED_ERROR,
} from '../../utils/user-form-rules.util';

const STREET_REQUIRED_ERROR = 'Rue requise';
const ZIP_CODE_REQUIRED_ERROR = 'Code postal requis';
const CITY_REQUIRED_ERROR = 'Ville requise';

const ADDRESS_DISCARDED_MESSAGE =
  'Cette adresse ne sera pas enregistrée : sélectionnez-la dans la liste proposée, avec son numéro, son code postal et sa ville';

const PREFERENCES = [
  { name: 'newsLetter', label: 'Newsletter' },
  { name: 'groupLabels', label: 'Étiquettes groupées' },
] as const;

const PASSWORD_HINT_ID = 'password-hint';
const PASSWORD_HINT =
  "Laissé vide, un mot de passe est généré, mais aucun e-mail n'est envoyé : c'est à vous de prévenir la personne qu'elle doit passer par « mot de passe oublié » pour accéder à son compte.";

interface CreateUserFormValues {
  role: string;
  firstname: string;
  lastname: string;
  email: string;
  phoneNumber: string;
  password: string;
  information: string;
  newsLetter: boolean;
  groupLabels: boolean;
  addressLabel: string;
  street: string;
  zipCode: string;
  city: string;
  addressTitle: string;
  addressComment: string;
}

const DEFAULT_VALUES: CreateUserFormValues = {
  role: UserRoles.USER,
  firstname: '',
  lastname: '',
  email: '',
  phoneNumber: '',
  password: '',
  information: '',
  newsLetter: true,
  groupLabels: true,
  addressLabel: '',
  street: '',
  zipCode: '',
  city: '',
  addressTitle: '',
  addressComment: '',
};

type StructuredAddress = Pick<CreateUserFormValues, 'street' | 'zipCode' | 'city'>;

type StructuredAddressErrors = Partial<Record<keyof StructuredAddress, string>>;

interface CreateUserDialogProps {
  onCreated: (user: User) => void;
}

const useStyles = makeStyles((theme) => ({
  createButton: {
    backgroundColor: theme.palette.admin.primary.main,
    color: theme.palette.admin.primary.contrastText,
    '&:hover': { backgroundColor: theme.palette.admin.primary.dark },
  },
  content: { display: 'flex', flexDirection: 'column', gap: theme.spacing(2) },
  row: { display: 'flex', gap: theme.spacing(2) },
  cityRow: {
    display: 'flex',
    gap: theme.spacing(2),
    '& > div:first-child': { width: '35%' },
    '& > div:last-child': { width: '65%' },
  },
  toggle: { minWidth: 44, minHeight: 44 },
  checkbox: {
    '&$checkboxChecked': {
      color: theme.palette.admin.primary.main,
    },
  },
  checkboxChecked: {},
}));

const getZipCodeError = (zipCode: string): string | undefined => {
  if (!zipCode) return ZIP_CODE_REQUIRED_ERROR;
  return ZIP_CODE_PATTERN.test(zipCode) ? undefined : ZIP_CODE_FORMAT_ERROR;
};

const getStructuredAddressErrors = ({ street, zipCode, city }: StructuredAddress): StructuredAddressErrors => ({
  street: street.trim() ? undefined : STREET_REQUIRED_ERROR,
  zipCode: getZipCodeError(zipCode),
  city: city.trim() ? undefined : CITY_REQUIRED_ERROR,
});

const buildAddress = (values: CreateUserFormValues): AdminCreateUserAddressDTO => {
  const title = values.addressTitle.trim();
  const comment = values.addressComment.trim();

  return {
    street: values.street.trim(),
    zipCode: values.zipCode,
    city: values.city.trim(),
    ...(title ? { title } : {}),
    ...(comment ? { comment } : {}),
  };
};

const buildPayload = (values: CreateUserFormValues, isPlaceSelected: boolean): AdminCreateUserRequestDTO => ({
  role: values.role,
  firstname: values.firstname.trim(),
  lastname: values.lastname.trim(),
  email: values.email,
  phoneNumber: values.phoneNumber,
  newsLetter: values.newsLetter,
  groupLabels: values.groupLabels,
  ...(values.password ? { password: values.password } : {}),
  ...(values.information.trim() ? { information: values.information.trim() } : {}),
  ...(isPlaceSelected ? { address: buildAddress(values) } : {}),
});

const CreateUserDialog: React.FC<CreateUserDialogProps> = ({ onCreated }) => {
  const classes = useStyles();
  const [isOpen, setIsOpen] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isPlaceSelected, setIsPlaceSelected] = useState(false);
  const { isSubmitting, createUser } = useAdminCreateUser();

  const { control, handleSubmit, reset, setValue } = useForm<CreateUserFormValues>({
    mode: 'onSubmit',
    defaultValues: DEFAULT_VALUES,
  });

  const lastSelectedAddressLabelRef = useRef<string | null>(null);

  const addressLabel = useWatch({ control, name: 'addressLabel' });
  const street = useWatch({ control, name: 'street' });
  const zipCode = useWatch({ control, name: 'zipCode' });
  const city = useWatch({ control, name: 'city' });
  const addressErrors: StructuredAddressErrors = isPlaceSelected
    ? getStructuredAddressErrors({ street, zipCode, city })
    : {};
  const isAddressInvalid = Object.values(addressErrors).some(Boolean);
  const isAddressDiscarded = Boolean(addressLabel) && !isPlaceSelected;

  const close = () => {
    if (isSubmitting) return;

    setIsOpen(false);
    setIsPasswordVisible(false);
    setIsPlaceSelected(false);
    lastSelectedAddressLabelRef.current = null;
    reset(DEFAULT_VALUES);
  };

  const applySelectedAddress = useCallback(
    (address: ParsedAddress) => {
      setValue('street', address.street);
      setValue('zipCode', address.zipCode);
      setValue('city', address.city);
      lastSelectedAddressLabelRef.current = buildAddressLabel(address);
      setIsPlaceSelected(true);
    },
    [setValue]
  );

  const handleAddressLabelChange = useCallback(
    (value: string) => {
      setValue('addressLabel', value);

      if (value !== lastSelectedAddressLabelRef.current) {
        setValue('street', '');
        setValue('zipCode', '');
        setValue('city', '');
        lastSelectedAddressLabelRef.current = null;
        setIsPlaceSelected(false);
      }
    },
    [setValue]
  );

  const onSubmit = handleSubmit(async (values) => {
    if (isAddressInvalid) return;

    const user = await createUser(buildPayload(values, isPlaceSelected));

    if (!user) return;

    onCreated(user);
    close();
  });

  return (
    <>
      <Button className={classes.createButton} variant="contained" size="small" onClick={() => setIsOpen(true)}>
        Créer un utilisateur
      </Button>

      <Dialog open={isOpen} onClose={close} fullWidth maxWidth="sm" aria-labelledby="create-user-dialog-title">
        <DialogTitle id="create-user-dialog-title">Créer un utilisateur</DialogTitle>

        <form onSubmit={onSubmit} noValidate>
          <DialogContent className={classes.content}>
            <Controller
              name="role"
              control={control}
              render={({ field }) => (
                <FormControl variant="outlined" size="small">
                  <InputLabel htmlFor="role">Rôle</InputLabel>
                  <Select native label="Rôle" {...field} inputProps={{ name: 'role', id: 'role' }}>
                    {CREATABLE_USER_ROLES.map((role) => (
                      <option key={role} value={role}>
                        {CREATABLE_USER_ROLE_LABELS[role]}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              )}
            />

            <div className={classes.row}>
              <Controller
                name="firstname"
                control={control}
                rules={{
                  required: FIRSTNAME_REQUIRED_ERROR,
                  validate: (value) => value.trim().length > 0 || FIRSTNAME_REQUIRED_ERROR,
                }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    inputRef={field.ref}
                    id="firstname"
                    autoFocus
                    fullWidth
                    size="small"
                    label="Prénom"
                    variant="outlined"
                    error={Boolean(error)}
                    helperText={error?.message}
                    inputProps={{ name: 'firstname' }}
                  />
                )}
              />
              <Controller
                name="lastname"
                control={control}
                rules={{
                  required: LASTNAME_REQUIRED_ERROR,
                  validate: (value) => value.trim().length > 0 || LASTNAME_REQUIRED_ERROR,
                }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    inputRef={field.ref}
                    id="lastname"
                    fullWidth
                    size="small"
                    label="Nom"
                    variant="outlined"
                    error={Boolean(error)}
                    helperText={error?.message}
                    inputProps={{ name: 'lastname' }}
                  />
                )}
              />
            </div>

            <Controller
              name="email"
              control={control}
              rules={{
                required: 'Veuillez saisir une adresse mail',
                validate: (value) => isValidEmail(value) || EMAIL_INVALID_ERROR,
              }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  inputRef={field.ref}
                  id="email"
                  fullWidth
                  size="small"
                  label="Adresse mail"
                  type="email"
                  variant="outlined"
                  error={Boolean(error)}
                  helperText={error?.message}
                  inputProps={{ name: 'email' }}
                />
              )}
            />

            <Controller
              name="phoneNumber"
              control={control}
              rules={{
                required: PHONE_NUMBER_REQUIRED_ERROR,
                validate: (value) => !isPhoneNumberIncomplete(value) || PHONE_NUMBER_LENGTH_ERROR,
              }}
              render={({ field, fieldState: { error } }) => (
                <PhoneNumberField
                  id="phoneNumber"
                  inputRef={field.ref}
                  value={field.value}
                  onChange={field.onChange}
                  error={Boolean(error)}
                  helperText={error?.message}
                  fullWidth
                />
              )}
            />

            <Controller
              name="addressLabel"
              control={control}
              render={({ field }) => (
                <AddressAutocomplete
                  id="create-user-address"
                  label="Adresse"
                  size="small"
                  value={field.value}
                  onChange={handleAddressLabelChange}
                  onAddressSelected={applySelectedAddress}
                  error={isAddressDiscarded}
                  helperText={isAddressDiscarded ? ADDRESS_DISCARDED_MESSAGE : undefined}
                />
              )}
            />

            <Controller
              name="street"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  inputRef={field.ref}
                  id="create-user-street"
                  fullWidth
                  size="small"
                  label="Rue"
                  variant="outlined"
                  disabled={!isPlaceSelected}
                  error={Boolean(addressErrors.street)}
                  helperText={addressErrors.street}
                  inputProps={{ name: 'street' }}
                />
              )}
            />

            <div className={classes.cityRow}>
              <Controller
                name="zipCode"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    inputRef={field.ref}
                    id="create-user-zip-code"
                    size="small"
                    label="Code postal"
                    variant="outlined"
                    disabled={!isPlaceSelected}
                    error={Boolean(addressErrors.zipCode)}
                    helperText={addressErrors.zipCode}
                    inputProps={{ name: 'zipCode', inputMode: 'numeric', maxLength: 5 }}
                  />
                )}
              />
              <Controller
                name="city"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    inputRef={field.ref}
                    id="create-user-city"
                    fullWidth
                    size="small"
                    label="Ville"
                    variant="outlined"
                    disabled={!isPlaceSelected}
                    error={Boolean(addressErrors.city)}
                    helperText={addressErrors.city}
                    inputProps={{ name: 'city' }}
                  />
                )}
              />
            </div>

            <div className={classes.row}>
              <Controller
                name="addressTitle"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    inputRef={field.ref}
                    id="create-user-address-title"
                    fullWidth
                    size="small"
                    label={ADDRESS_TITLE_LABEL}
                    variant="outlined"
                    disabled={!isPlaceSelected}
                    inputProps={{ name: 'addressTitle' }}
                  />
                )}
              />
              <Controller
                name="addressComment"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    inputRef={field.ref}
                    id="create-user-address-comment"
                    fullWidth
                    size="small"
                    label={ADDRESS_COMMENT_LABEL}
                    variant="outlined"
                    disabled={!isPlaceSelected}
                    inputProps={{ name: 'addressComment', maxLength: 250 }}
                  />
                )}
              />
            </div>

            <Controller
              name="information"
              control={control}
              rules={{ maxLength: { value: 250, message: '250 caractères au maximum' } }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  inputRef={field.ref}
                  id="information"
                  fullWidth
                  size="small"
                  label="Complément de profil"
                  variant="outlined"
                  error={Boolean(error)}
                  helperText={error?.message}
                  inputProps={{ name: 'information' }}
                />
              )}
            />

            <Controller
              name="password"
              control={control}
              rules={{
                minLength: { value: 8, message: 'Huit caractères au minimum' },
                maxLength: { value: 64, message: '64 caractères au maximum' },
              }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  inputRef={field.ref}
                  id="password"
                  fullWidth
                  size="small"
                  label="Mot de passe"
                  type={isPasswordVisible ? 'text' : 'password'}
                  variant="outlined"
                  error={Boolean(error)}
                  helperText={error?.message}
                  inputProps={{ name: 'password', 'aria-describedby': PASSWORD_HINT_ID }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          className={classes.toggle}
                          aria-label={isPasswordVisible ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                          onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                        >
                          {isPasswordVisible ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />

            <Typography id={PASSWORD_HINT_ID} variant="caption" color="textSecondary">
              {PASSWORD_HINT}
            </Typography>

            <div className={classes.row}>
              {PREFERENCES.map((preference) => (
                <Controller
                  key={preference.name}
                  name={preference.name}
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      label={preference.label}
                      control={
                        <Checkbox
                          size="small"
                          color="default"
                          classes={{ root: classes.checkbox, checked: classes.checkboxChecked }}
                          inputRef={field.ref}
                          name={preference.name}
                          checked={field.value}
                          onChange={(event) => field.onChange(event.target.checked)}
                        />
                      }
                    />
                  )}
                />
              ))}
            </div>
          </DialogContent>

          <DialogActions>
            <Button variant="outlined" size="small" onClick={close} disabled={isSubmitting}>
              Annuler
            </Button>
            <Button
              className={classes.createButton}
              variant="contained"
              size="small"
              type="submit"
              disabled={isSubmitting || isAddressInvalid}
              startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {isSubmitting ? 'Création...' : 'Créer'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
};

export default CreateUserDialog;
